/**
 * Placement presets for the faint AI-model logos that drift in the GPT bottom
 * CTA background. Curated named anchors (rather than free-form offsets) keep the
 * Studio field approachable while still letting editors scatter logos around the
 * section. Each value is the Tailwind placement applied to an icon's wrapper.
 *
 * The logos themselves come from `AI_MODEL_BRANDS`, so the CTA reuses the exact
 * same artwork (and `FloatingIcon` renderer) as the prominent "AI models" band.
 */
export const GPT_CTA_ICON_POSITIONS = {
  "top-left": "left-[4%] top-[14%] sm:left-[10%] md:left-[15%]",
  "top-right": "right-[5%] top-[8%] sm:right-[11%] md:right-[16%]",
  "mid-left": "left-[3%] top-1/2 -translate-y-1/2 md:left-[8%]",
  "mid-right": "right-[3%] top-1/2 -translate-y-1/2 md:right-[8%]",
  "bottom-left": "bottom-[16%] left-[6%] sm:left-[10%] md:left-[14%]",
  "bottom-right": "bottom-[14%] right-[10%] sm:right-[14%] md:right-[21%]",
} as const;

export type GptCtaIconPosition = keyof typeof GPT_CTA_ICON_POSITIONS;

/** Default box size (px) for a newly added background logo. */
export const GPT_CTA_ICON_DEFAULT_SIZE = 64;

/**
 * Float stagger (seconds) applied by index so a cluster of logos drifts out of
 * sync. Negative values desync without delaying the first paint.
 */
export const GPT_CTA_ICON_FLOAT_DELAYS = [0, -2.4, -3.8, -1.4, -5.1];

/** Select-field options (Studio) for a background logo's position. */
export const GPT_CTA_ICON_POSITION_OPTIONS = (
  Object.keys(GPT_CTA_ICON_POSITIONS) as GptCtaIconPosition[]
).map((value) => ({
  label: value
    .split("-")
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(" "),
  value,
}));

/** Resolve a stored position to its Tailwind placement, tolerating bad data. */
export const getGptCtaIconPosition = (position: string): string =>
  GPT_CTA_ICON_POSITIONS[position as GptCtaIconPosition] ??
  GPT_CTA_ICON_POSITIONS["top-left"];
